import {
  Component,
  OnInit,
  Input,
  ViewChild,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { InvestmentWizardService } from '../../investment-wizard/services/investment-wizard-service.service';
import {
  switchMap,
  distinctUntilChanged,
  map,
  debounceTime,
  tap,
  finalize,
} from 'rxjs/operators';
import {
  MatAutocompleteModule,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { SessionStorageService } from '../../services/session-storage.service';
import { FundFamilySearchComponent } from './fund-family-search/fund-family-search.component';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-fund-search',
  templateUrl: './fund-search.component.html',
  styleUrls: ['./fund-search.component.scss'],
  imports: [
    FundFamilySearchComponent,
    ReactiveFormsModule,
    MatInputModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    MatExpansionModule,
    MatSelectModule,
    MatIconModule,
  ],
})
export class FundSearchComponent implements OnInit, OnChanges {
  private iws = inject(InvestmentWizardService);
  private ss = inject(SessionStorageService);
  private changeDetector = inject(ChangeDetectorRef);

  @ViewChild('symbolLookup', { static: false })
  symbolComplete: MatAutocompleteTrigger;
  @Input() type: string;
  @Input() options;
  @Input() program;
  @Input() fiveTwoNinePlan;
  @Input() search529;
  @Input() parentField;
  @Output() fundSelected = new EventEmitter();

  searchControl = new FormControl();
  searchControl529 = new FormControl('');
  filteredSymbols;
  symbols;
  label;
  funds529;

  shareClass = 'A';
  fundFamily;
  fundFamilySearch: string | null = null;

  familyList;

  rights = [];
  dropdownLoading = false;
  dropdownNoResults = false;

  ngOnInit(): void {
    if (this.ss.get('rights').includes('ReadOnlyAccess')) {
      this.searchControl.disable();
      this.searchControl529.disable();
    }
    if (!this.search529) {
      this.symbolSearch();
      if (this.options?.fundfamily || this.options?.shareClass) {
        this.iws.fundFamilyList().subscribe(data => {
          this.familyList = data.results;
        });
      }
    } else if (this.search529 && !this.fiveTwoNinePlan) {
      this.searchControl529.disable();
    } else if (this.search529 && this.fiveTwoNinePlan && !this.symbols) {
      this.symbolSearch529();
    }

    this.rights = this.ss.get('rights');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      Object.prototype.hasOwnProperty.call(changes, 'fiveTwoNinePlan') &&
      !changes.fiveTwoNinePlan.firstChange
    ) {
      this.searchControl529.disable();
      this.symbols = null;
      this.symbolSearch529();
    }
  }

  selectFundLookup(ev) {
    this.searchControl.setValue('');
    this.searchControl529.setValue('');
    this.fundSelected.emit(ev);
  }

  symbolSearch(preSearch = false) {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(500),
        tap(() => {
          this.dropdownNoResults = false;
          this.dropdownLoading = true;
          this.changeDetector.detectChanges();
        }),
        distinctUntilChanged(),
        switchMap((value: string) => {
          if (value || preSearch) {
            return this._filterSymbols(value).pipe(
              finalize(() => {
                this.dropdownLoading = false;
                this.changeDetector.detectChanges();
              })
            );
          } else if (!value) {
            this.dropdownLoading = false;
            this.filteredSymbols = null;
            this.changeDetector.detectChanges();
          }
          return [];
        })
      )
      .subscribe(results => {
        if (!results) {
          this.dropdownNoResults = true;
        } else {
          this.filteredSymbols = results;
          this.dropdownNoResults = false;
        }
        this.changeDetector.detectChanges();
      });
  }

  symbolSearch529() {
    this.searchControl529.disable();
    const types = this.options['fundtype'].toString().replace(',', '');
    this.iws
      .fundSearch(null, this.program, null, null, types, this.fiveTwoNinePlan)
      .subscribe(x => {
        if (!this.ss.get('rights').includes('ReadOnlyAccess'))
          this.searchControl529.enable();
        this.symbols = x.results;
        this.filteredSymbols = x.results;
      });

    this.searchControl529.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((value: string) => {
        if (value) {
          this.dropdownNoResults = false;
          this.filteredSymbols = this.symbols.filter(symbol =>
            symbol.FundName.toLowerCase().includes(value.toLowerCase())
          );
          this.changeDetector.detectChanges();
          if (this.filteredSymbols.length === 0) {
            this.dropdownNoResults = true;
          }
        } else if (!value) {
          this.dropdownNoResults = false;
          this.filteredSymbols = this.symbols;
          this.changeDetector.detectChanges();
        }
      });
  }

  private _filterSymbols(value: string, preSearch = false) {
    const types = this.options['fundtype'].toString().replace(',', '');
    return this.iws
      .fundSearch(
        value,
        this.program,
        this.parentField?.expressions['showFundFamilySearch']?.()
          ? this.fundFamilySearch
          : null,
        null,
        types,
        this.fiveTwoNinePlan
      )
      .pipe(
        map(x => {
          if (x.results && x.results.length > 0) {
            return x.results;
          }
        })
      );
  }

  displayFn(option?): string | undefined {
    return option ? `${option.FundID} - ${option.FundName}` : undefined;
  }

  setFundFamilySearch(ev) {
    this.fundFamilySearch = ev;
    if (this.fundFamilySearch) this.searchControl.enable();
  }
}
